import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FileText, Download, Home } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { RichTextDisplay } from '../components/ui/rich-text-display';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function CustomPage() {
  const { slug } = useParams();
  const [pageData, setPageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    const loadPage = async () => {
      setLoading(true);
      setNotFound(false);
      try {
        const response = await axios.get(`${API_URL}/api/pages/${slug}`);
        setPageData(response.data);
      } catch (error) {
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    };
    loadPage();
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center" data-testid="custom-page-loading">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center" data-testid="custom-page-not-found">
        <div className="text-center px-4">
          <h1 className="font-display font-bold text-4xl mb-4">Pagina non trovata</h1>
          <p className="text-muted-foreground mb-8">La pagina che stai cercando non esiste o è stata rimossa.</p>
          <Button asChild data-testid="back-home-btn">
            <Link to="/">
              <Home className="w-4 h-4 mr-2" />
              Torna alla Home
            </Link>
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20" data-testid={`custom-page-${slug}`}>
      {/* Hero Section */}
      <section className="relative py-16 bg-gradient-dark overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-primary rounded-full blur-3xl"></div>
        </div>

        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-4xl mx-auto text-center"
          >
            <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-primary to-red-600 flex items-center justify-center mx-auto mb-6 shadow-lg">
              <FileText className="w-10 h-10 text-white" />
            </div>
            <h1 className="font-display font-bold text-4xl lg:text-5xl mb-4 text-white" data-testid="custom-page-title">
              {pageData.title}
            </h1>
          </motion.div>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-16 bg-background">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              <Card className="glass-card">
                <CardContent className="p-8 md:p-12">
                  {pageData.pdf_url && (
                    <div className="mb-8 p-4 rounded-lg bg-primary/10 border border-primary/20 flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <FileText className="w-6 h-6 text-primary" />
                        <div>
                          <p className="font-medium">Documento PDF disponibile</p>
                          <p className="text-sm text-muted-foreground">Scarica la versione completa in PDF</p>
                        </div>
                      </div>
                      <Button asChild data-testid="download-pdf-btn">
                        <a href={pageData.pdf_url} target="_blank" rel="noopener noreferrer">
                          <Download className="w-4 h-4 mr-2" />
                          Scarica PDF
                        </a>
                      </Button>
                    </div>
                  )}

                  <div className="prose prose-invert max-w-none">
                    <RichTextDisplay content={pageData.content} />
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
}
